import { profile2OrientCopy } from "building-common/profile2Utils";
import type { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import type { OpeningSide, OpeningShapes } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type { HeadCasingSettings } from "building-types/settings/headCasingSettings";
import {
  ApronCasingType,
  SideCasingType,
  StoolCasingType,
  HeadCasingType,
} from "building-types/settings/settingsTypesEnum";
import type {
  AllWindowViewNoteTable,
  ExteriorWindowCasingViewNoteTable,
} from "building-types/settings/viewNotesSettings";
import { ExteriorWindowCasingViewNoteType } from "building-types/settings/viewNotesSettings";
import type { WainscotingSettings } from "building-types/settings/wainscotingSettings";
import { PolygonWindowMode, WindowShapeTypes } from "building-types/settings/windowTypesEnum";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import type { WindowDescriptor, WindowUnit } from "building-types/windows/windowTypes";
import uid from "common/utils/uid";
import { roomIsEffectivelyExterior } from "doc/rooms/roomQuery";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { faceNew } from "geom/brep";
import { REMOVE_COPLANAR_LINES_EPSILON } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongNormal } from "geom/extrude";
import { planeMoveAlongNormal, planeCopy, planeReProjectPolyLine2, planeDistanceTo } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2Reverse,
  polyLine2Edge,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2Copy,
  polyLine2ReverseCopy,
  polyLine2SetCW,
  polyLine2AABB2,
  polyLine2Reflect,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3AABB3 } from "geom/polyLine3";
import { ray2New } from "geom/ray2";
import { makeRectFromLine } from "geom/rect2";
import { headCasingRemoveCorner } from "../casing/casingQuery";
import { addHeadCasing } from "../headcasing";
import { offsetCasingShapes } from "../openings/offsetCasing";
import { checkOrientationWithMode } from "../streetOrientation";
import { archRadiusEqualsWindowWidth } from "./windowUtils";

export function makeExteriorWindowCasing(
  windowDescriptor: WindowDescriptor,
  wallOpening: WallOpening,
  backFacePlane: Plane,
  backOutline3: PolyLine3,
  frontFacePlane: Plane,
  frontOutline3: PolyLine3,
  trimExtend: number,
  streetOrientation: StreetOrientation,
  wainscotingSettings: WainscotingSettings,
  exteriorWindowCasingSettings: ExteriorWindowCasingSettings,
  windowViewNoteTable: AllWindowViewNoteTable,
  unit: WindowUnit,
  backShapes: OpeningShapes,
  frontShapes: OpeningShapes,
) {
  let { front, back } = wallOpening;

  if (front.context.condition === "EXTERIOR") {
    const { outlineReversed, arcCurve, arcCurveSimple } = frontShapes;
    if (outlineReversed) {
      if (!roomIsEffectivelyExterior(wallOpening.backRoom, false)) {
        // @todo(Andrew): figure out where this is flipped elsewhere
        [front, back] = [back, front];
      }

      if (front.context.condition === "EXTERIOR") {
        makeSideExteriorWindowCasing(
          back,
          front,
          windowDescriptor,
          frontFacePlane,
          outlineReversed,
          backFacePlane,
          backOutline3,
          arcCurve,
          arcCurveSimple,
          trimExtend,
          streetOrientation,
          wainscotingSettings,
          exteriorWindowCasingSettings,
          windowViewNoteTable,
          unit,
        );
      }
    }
  }
  if (back.context.condition === "EXTERIOR") {
    const { outlineReversed, arcCurve, arcCurveSimple } = backShapes;
    if (outlineReversed) {
      makeSideExteriorWindowCasing(
        front,
        back,
        windowDescriptor,
        backFacePlane,
        outlineReversed,
        frontFacePlane,
        frontOutline3,
        arcCurve,
        arcCurveSimple,
        trimExtend,
        streetOrientation,
        wainscotingSettings,
        exteriorWindowCasingSettings,
        windowViewNoteTable,
        unit,
      );
    }
  }
}

function makeSideExteriorWindowCasing(
  side: OpeningSide,
  sideOpposite: OpeningSide,
  windowDescriptor: WindowDescriptor,
  facePlaneOpposite: Plane,
  outlineOppositeRev: PolyLine2,
  facePlane: Plane,
  outline3: PolyLine3,
  arcCurve: PolyLine2 | undefined,
  arcCurveSimple: PolyLine2 | undefined,
  trimExtend: number,
  streetOrientation: StreetOrientation,
  wainscotingSettings: WainscotingSettings,
  exteriorWindowCasingSettings: ExteriorWindowCasingSettings,
  windowViewNoteTable: AllWindowViewNoteTable,
  unit: WindowUnit,
) {
  outlineOppositeRev = profile2OrientCopy(outlineOppositeRev);
  const {
    apronCasingType,
    sideCasingType,
    headCasingType,
    stoolCasingType,
    sideCasingDepth = 0,
    apronDepth = 0,
  } = exteriorWindowCasingSettings;
  const { shape: windowShape } = windowDescriptor;

  const casingThicknessEpsilon = REMOVE_COPLANAR_LINES_EPSILON + DIST_EPSILON;
  const sideCasingThickness = sideCasingDepth - casingThicknessEpsilon;

  const { casingHorizontalOffset = 0, casingLateralOffset = 0 } = exteriorWindowCasingSettings;

  if (casingLateralOffset !== 0) {
    facePlaneOpposite = planeMoveAlongNormal(planeCopy(facePlaneOpposite), casingLateralOffset);
  }

  if (casingHorizontalOffset !== 0) {
    ({
      arcCurve,
      arcCurveSimple,
      casingOutline: outlineOppositeRev,
    } = offsetCasingShapes(casingHorizontalOffset, {
      arcCurve,
      arcCurveSimple,
      casingOutline: outlineOppositeRev,
    }));

    side.opening2 = outlineOppositeRev;
  }

  outlineOppositeRev = profile2OrientCopy(polyLine2SetCW(outlineOppositeRev));

  //make apron casing
  if (
    apronCasingType === ApronCasingType.TRIM &&
    windowShape !== WindowShapeTypes.CIRCLE &&
    windowShape !== WindowShapeTypes.HALF_ROUND &&
    windowShape !== WindowShapeTypes.ELLIPSE
  ) {
    //view note
    const apronCasingDescriptor = viewEntityDescriptorNew(
      uid(),
      ViewEntityType.APRON_CASING,
      windowViewNoteTable[ExteriorWindowCasingViewNoteType.APRON],
    );

    addApronCasing(
      sideOpposite,
      facePlaneOpposite,
      apronDepth,
      exteriorWindowCasingSettings,
      apronCasingDescriptor,
      unit,
    );
  }

  //make side casing
  if (sideCasingType === SideCasingType.TRIM) {
    //view note
    const sideCasingDescriptor = viewEntityDescriptorNew(
      uid(),
      ViewEntityType.SIDE_CASING,
      windowViewNoteTable[ExteriorWindowCasingViewNoteType.SIDE],
    );

    addWindowSideCasing(
      sideOpposite,
      facePlaneOpposite,
      sideCasingThickness,
      exteriorWindowCasingSettings,
      sideCasingDescriptor,
      unit,
    );
  }

  //make head casing
  if (
    headCasingType !== HeadCasingType.NONE &&
    windowShape !== WindowShapeTypes.CIRCLE &&
    windowShape !== WindowShapeTypes.HALF_ROUND &&
    windowShape !== WindowShapeTypes.ELLIPSE
  ) {
    //get generation geometry
    let headcasingLine = polyLine2Edge(outlineOppositeRev, 2);

    polyLine2ExtendEnd(headcasingLine, trimExtend);
    polyLine2ExtendStart(headcasingLine, trimExtend);

    let frontPlane = planeCopy(facePlane);
    let backPlane = planeCopy(facePlaneOpposite);

    //full arch errors
    let arcCurveError = false;
    if ((exteriorWindowCasingSettings as HeadCasingSettings)?.headCasingType === HeadCasingType.FULL_ARCH) {
      const tmpPlane = frontPlane;
      frontPlane = backPlane;
      backPlane = tmpPlane;

      const tmpHole = side;
      side = sideOpposite;
      sideOpposite = tmpHole;

      if (!arcCurve) {
        arcCurveError = true;
      } else {
        arcCurve = polyLine2Reverse(polyLine2Copy(arcCurve));
      }
    }

    //unsuppored headcasing for window shape
    let unsupportedHeadCasing = false;
    if (
      ((windowShape === WindowShapeTypes.RECTANGLE && windowDescriptor.arched) ||
        windowShape === WindowShapeTypes.RECTANGLE_ARCHED_TOP) &&
      (headCasingType === HeadCasingType.SLICES ||
        headCasingType === HeadCasingType.STONE ||
        headCasingType === HeadCasingType.JACK_ARCH ||
        headCasingType === HeadCasingType.ARCHED_SLICES ||
        headCasingType === HeadCasingType.EYEBROW_TRIM)
    ) {
      unsupportedHeadCasing = true;
    }

    if (windowShape === WindowShapeTypes.POLYGONAL) {
      if (headCasingType !== HeadCasingType.TRIM) {
        unsupportedHeadCasing = true;
      }
      if (windowDescriptor.polygonWindowMode === PolygonWindowMode.PENTAGONAL) {
        const pl = polyLine2Copy(outlineOppositeRev);
        headcasingLine = polyLine2New([pl[4], pl[5], pl[6], pl[7], pl[8], pl[9]]);
      }
    }

    let useArcCurveSimple = false;
    if (sideCasingType === SideCasingType.DISABLED) {
      if (headCasingRemoveCorner(exteriorWindowCasingSettings) && !archRadiusEqualsWindowWidth(windowDescriptor)) {
        useArcCurveSimple = true;
      }
    }

    if (arcCurveError) {
      console.warn("Window Full Arc curve is null and cannot produce FULL_ARCH_CASING!");
    } else if (!unsupportedHeadCasing) {
      const apronLine = polyLine2Reverse(polyLine2Edge(outlineOppositeRev, 0));

      const { originX: bx, originY: by, originZ: bz } = backPlane;

      const wallWidth = planeDistanceTo(frontPlane, bx, by, bz);

      addHeadCasing({
        profiles: sideOpposite,
        bottomLine: headcasingLine,
        apronLine,
        arch: useArcCurveSimple ? arcCurveSimple : arcCurve,
        casingPlane: frontPlane,
        openingPlane: backPlane,
        wallWidth,
        settings: exteriorWindowCasingSettings as HeadCasingSettings,
        extrusions: unit.decorations,
        viewNotes: windowViewNoteTable as ExteriorWindowCasingViewNoteTable,
      });
    }
  }

  //make stool casing
  if (
    stoolCasingType === StoolCasingType.TRIM &&
    windowShape !== WindowShapeTypes.CIRCLE &&
    windowShape !== WindowShapeTypes.HALF_ROUND &&
    windowShape !== WindowShapeTypes.ELLIPSE
  ) {
    //view note
    const stoolViewEntityDescriptor = viewEntityDescriptorNew(
      uid(),
      ViewEntityType.STOOL_CASING,
      windowViewNoteTable[ExteriorWindowCasingViewNoteType.STOOL],
    );

    addWindowStoolCasing(
      sideOpposite,
      outline3,
      facePlaneOpposite,
      facePlane,
      exteriorWindowCasingSettings,
      streetOrientation,
      wainscotingSettings,
      stoolViewEntityDescriptor,
      unit,
    );
  }
}

function addApronCasing(
  profiles: OpeningSide,
  exteriorFacePlane: Plane,
  casingThickness: number,
  exteriorWindowCasingSettings: ExteriorWindowCasingSettings,
  viewNoteDescriptor: ViewEntityDescriptor,
  unit: WindowUnit,
) {
  const { apronCasingMaterial } = exteriorWindowCasingSettings;

  if (!profiles.apronCasing) {
    return;
  }
  const apronFace = faceNew([profiles.apronCasing], exteriorFacePlane);
  if (!apronFace) {
    return;
  }
  const apron = extrudeFaceAlongNormal(apronFace, casingThickness);

  unit.exterior.add(
    apron,
    exteriorMaterialOverrideNew(apronCasingMaterial ?? "trimExterior0", undefined, viewNoteDescriptor),
  );
}

function addWindowSideCasing(
  profiles: OpeningSide,
  exteriorFacePlane: Plane,
  casingThickness: number,
  exteriorWindowCasingSettings: ExteriorWindowCasingSettings,
  viewNoteDescriptor: ViewEntityDescriptor,
  unit: WindowUnit,
) {
  const { sideCasingMaterial } = exteriorWindowCasingSettings;

  //material
  const mat = exteriorMaterialOverrideNew(sideCasingMaterial ?? "trimExterior0", undefined, viewNoteDescriptor);

  // @todo(Andrew): this is a temporary fix until mirroring is ironed out for openings
  const aabb2 = polyLine2AABB2(profiles.openingRect2);
  const centerX = (aabb2.maxX + aabb2.minX) / 2;
  const mirror2 = (p: PolyLine2) => {
    p = polyLine2Copy(p);
    polyLine2Reflect(p, ray2New(centerX, 0, 0, 1));
    return polyLine2SetCW(p);
  };

  for (let profile of profiles.sideCasing) {
    if (unit.mirror) {
      profile = profile.map(mirror2);
    }
    const casingFace = faceNew(profile, exteriorFacePlane);
    const trim = extrudeFaceAlongNormal(casingFace, casingThickness);
    unit.exterior.add(trim, mat);
  }
}

function addWindowStoolCasing(
  profiles: OpeningSide,
  interiorOutline3: PolyLine2,
  exteriorFacePlane: Plane,
  interiorFacePlane: Plane,
  exteriorWindowCasingSettings: ExteriorWindowCasingSettings,
  streetOrientation: StreetOrientation,
  wainscotingSettings: WainscotingSettings,
  viewNoteDescriptor: ViewEntityDescriptor,
  unit: WindowUnit,
) {
  const {
    stoolCasingDepth = 0,
    stoolCasingMaterial,
    stoolCasingExtension = 0,
    stoolCasingThickness = 0,
    sideCasingWidth = 0,
  } = exteriorWindowCasingSettings;

  //stool depth
  const aabb3 = polyLine3AABB3(interiorOutline3);
  let minStoolDepth = 0.0;

  if (
    checkOrientationWithMode(streetOrientation, wainscotingSettings.wainscotingMode) &&
    wainscotingSettings.height > aabb3.minZ
  ) {
    minStoolDepth = wainscotingSettings.thickness + 1.0;
  }

  const stoolDepth = Math.max(stoolCasingDepth, minStoolDepth);

  let stoolBase = profiles.stoolCasing;
  // @todo(Andrew) remove this duplicated code
  if (!stoolBase) {
    const stoolLine = polyLine2Edge(polyLine2ReverseCopy(profiles.opening2), -1);
    const trimExtend = exteriorWindowCasingSettings.sideCasingType === SideCasingType.DISABLED ? 0 : sideCasingWidth;

    // stool geometry
    polyLine2ExtendStart(stoolLine, trimExtend + stoolCasingExtension);
    polyLine2ExtendEnd(stoolLine, trimExtend + stoolCasingExtension);
    stoolBase = makeRectFromLine(stoolLine, 0, stoolCasingThickness);

    const stoolFace = faceNew([stoolBase], exteriorFacePlane);
    const stool = extrudeFaceAlongNormal(stoolFace, stoolDepth);
    unit.exterior.add(
      stool,
      exteriorMaterialOverrideNew(stoolCasingMaterial ?? "trimExterior0", undefined, viewNoteDescriptor),
    );
    return;
  }

  if (unit.windowPlacement.reverse) {
    stoolBase = planeReProjectPolyLine2(stoolBase, interiorFacePlane, exteriorFacePlane);
  }

  const stoolFace = faceNew([stoolBase], exteriorFacePlane);

  const stool = extrudeFaceAlongNormal(stoolFace, stoolDepth);
  unit.exterior.add(
    stool,
    exteriorMaterialOverrideNew(stoolCasingMaterial ?? "trimExterior0", undefined, viewNoteDescriptor),
  );
}
